/**
 * Utility functions for parsing and formatting strings.
 * This module provides a set of common masks used in Brazilian forms,
 * such as CPF, CNPJ, Phone, Currency, etc.
 */

// --- Helpers ---

const MAX_NUMERIC_LENGTH = 15
const MAX_GENERIC_NUMBER_LENGTH = 64
const BRAZIL_DDI = '55'
const BRAZIL_DDI_LENGTH = 2
const PHONE_MAX_LENGTH = 11
const PHONE_LANDLINE_LENGTH = 10
const CPF_LENGTH = 11
const CNPJ_LENGTH = 14
const CEP_LENGTH = 8
const CREDIT_CARD_LENGTH = 16
const LICENSE_PLATE_LENGTH = 7
const MAX_MINUTES = 59
const MAX_HOURS = 23
const MONTHS_IN_YEAR = 12
const DAYS_IN_MONTH_MAX = 31
const PAD_LENGTH_2 = 2
const DATE_LENGTH = 8
const DATE_HALF_LENGTH = 3
const TIME_LENGTH = 4
const TIME_HALF_LENGTH = 3
const TIME_SLICE_START = 2
const LENGTH_1 = 1
const CEP_HYPHEN_INDEX = 5
const PLATE_HYPHEN_INDEX = 3
const MERCOSUR_OLD_PLATE_INDEX = 4
const DIVISOR_100 = 100
const RADIX_10 = 10
const FRACTION_DIGITS_2 = 2
const MIN_DAY = 1
const MIN_MONTH = 1
const MONTH_FEBRUARY = 2
const MIN_UNAMBIGUOUS_DAY = 3
const MIN_UNAMBIGUOUS_MONTH = 1
const MIN_UNAMBIGUOUS_HOUR = 2
const MIN_UNAMBIGUOUS_MINUTE = 5
const DATE_SLICE_DAY = 2
const DATE_SLICE_MONTH = 4
const DATE_MONTH_PREFIX_INDEX = 2
const MAX_DAY_FEBRUARY = 29
const MAX_DAY_30 = 30
const MAX_DAY_FEBRUARY_NON_LEAP = 28
const LEAP_YEAR_MOD_4 = 4
const LEAP_YEAR_MOD_100 = 100
const LEAP_YEAR_MOD_400 = 400
const CPF_VALIDATION_MOD = 11
const CPF_WEIGHT_START_1 = 11
const CPF_WEIGHT_START_2 = 12
const CPF_CHECK_DIGIT_MULTIPLIER = 10
const CPF_DIGIT_1_INDEX = 9
const CPF_DIGIT_2_INDEX = 10
const CNPJ_VALIDATION_MOD = 11
const CNPJ_VALIDATION_LIMIT = 2
const CNPJ_ALL_ZEROS = '00000000000000'
const CNPJ_UNITS_ONLY_LENGTH = 12
const CNPJ_WITH_DIGIT_1_LENGTH = 13
// biome-ignore lint/style/noMagicNumbers: Specific algorithm weights
const CNPJ_WEIGHTS_1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
// biome-ignore lint/style/noMagicNumbers: Specific algorithm weights
const CNPJ_WEIGHTS_2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
const CHAR_CODE_ZERO = 48
// biome-ignore lint/style/noMagicNumbers: Specific month indices
const MONTHS_30_DAYS = [4, 6, 9, 11]

/**
 * Removes all non-numeric characters from a string.
 */
function onlyNumbers(value: string | number): string {
  return String(value).replace(/\D/g, '')
}

/**
 * Removes all non-alphanumeric characters from a string.
 */
function onlyAlphanumeric(value: string | number): string {
  return String(value).replace(/[^a-zA-Z0-9]/g, '')
}

/**
 * Formats a number string into a currency or decimal format.
 * @param value The value to format.
 * @param options Intl.NumberFormat options.
 */
function formatNumber(value: string | number, options: Intl.NumberFormatOptions = {}): string {
  // Handle number type: treat as final float value (e.g., 8.00 → R$ 8,00)
  if (typeof value === 'number') {
    return new Intl.NumberFormat('pt-BR', options).format(value)
  }

  const stringValue = String(value)

  // Handle empty values
  if (!stringValue || stringValue.trim() === '') return ''

  // Check if string contains decimal separator (. or ,) followed by 1-2 digits
  // This indicates a float string like "8.00" or "8,50"
  const decimalPattern = /^-?\d+[.,]\d{1,2}$/
  if (decimalPattern.test(stringValue.trim())) {
    // Parse as float (replace comma with dot for parsing)
    const floatValue = Number.parseFloat(stringValue.replace(',', '.'))
    return new Intl.NumberFormat('pt-BR', options).format(floatValue)
  }

  // For digit-only strings, treat as cents (typing behavior)
  // e.g., "800" → 8.00
  const numericValue = onlyNumbers(stringValue).slice(0, MAX_NUMERIC_LENGTH)
  if (!numericValue) return ''

  const floatValue = Number(numericValue) / DIVISOR_100

  return new Intl.NumberFormat('pt-BR', options).format(floatValue)
}

// --- Parsers (Cleaners) ---

const parseToNumbers = (value: string) => onlyNumbers(value)
const parseToAlphanumeric = (value: string) => onlyAlphanumeric(value)

const parseToFloat = (value: string) => {
  const numericValue = onlyNumbers(value).slice(0, MAX_NUMERIC_LENGTH)
  if (!numericValue) return ''
  return (Number(numericValue) / DIVISOR_100).toFixed(PAD_LENGTH_2)
}

// --- Formatters ---

export const cpf = {
  format: (value: string) => {
    return onlyNumbers(value)
      .slice(0, CPF_LENGTH)
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1')
  },
  parse: parseToNumbers,
}

export const cnpj = {
  format: (value: string) => {
    // CNPJ will be alphanumeric starting July 2026.
    // Format: XX.XXX.XXX/XXXX-XX
    return onlyAlphanumeric(value)
      .slice(0, CNPJ_LENGTH)
      .toUpperCase()
      .replace(/^([A-Z0-9]{2})([A-Z0-9])/, '$1.$2')
      .replace(/^([A-Z0-9]{2})\.([A-Z0-9]{3})([A-Z0-9])/, '$1.$2.$3')
      .replace(/\.([A-Z0-9]{3})([A-Z0-9])/, '.$1/$2')
      .replace(/\/([A-Z0-9]{4})([A-Z0-9])/, '/$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1') // Last 2 digits remain numeric check digits
  },
  parse: parseToAlphanumeric,
}

export const cpfCnpj = {
  format: (value: string) => {
    const cleanValue = onlyAlphanumeric(value)
    // If it has letters, it must be CNPJ
    if (/[a-zA-Z]/.test(cleanValue)) {
      return cnpj.format(cleanValue)
    }
    // If length > CPF_LENGTH, strictly numeric, treat as CNPJ (legacy or new numeric CNPJ)
    if (cleanValue.length > CPF_LENGTH) {
      return cnpj.format(cleanValue)
    }
    // Otherwise try CPF
    return cpf.format(cleanValue)
  },
  parse: parseToAlphanumeric,
}

export const cep = {
  format: (value: string) => {
    return onlyNumbers(value)
      .slice(0, CEP_LENGTH)
      .replace(new RegExp(`(\\d{${CEP_HYPHEN_INDEX}})(\\d)`), '$1-$2')
      .replace(/(-\d{3})\d+?$/, '$1')
  },
  clean: (value: string | null | undefined) => {
    if (!value) return ''
    return onlyNumbers(value).slice(0, CEP_LENGTH)
  },
  parse: parseToNumbers,
}

export const phone = {
  format: (value: string) => {
    if (!value) return ''
    // Inline regex replacer to be 100% safe against external helpers
    const clean = String(value).replace(/\D/g, '')

    // Handle +55 prefix (Brazil DDI)
    // If starts with 55 and has > 11 digits (meaning 12 or 13), strip it
    let numericValue = clean
    if (numericValue.startsWith(BRAZIL_DDI) && numericValue.length > PHONE_MAX_LENGTH) {
      numericValue = numericValue.slice(BRAZIL_DDI_LENGTH)
    }

    // Limit to max mobile length (DDD + 9 digits)
    const finalValue = numericValue.slice(0, PHONE_MAX_LENGTH)

    if (finalValue.length <= PHONE_LANDLINE_LENGTH) {
      // Landline: (XX) XXXX-XXXX
      return finalValue
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2')
        .replace(/(-\d{4})\d+?$/, '$1')
    }

    // Mobile: (XX) XXXXX-XXXX
    return finalValue
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1')
  },
  parse: (value: string) => {
    if (!value) return ''
    const clean = String(value).replace(/\D/g, '')

    // Handle +55 prefix (Brazil DDI)
    let numericValue = clean
    if (numericValue.startsWith(BRAZIL_DDI) && numericValue.length > PHONE_MAX_LENGTH) {
      numericValue = numericValue.slice(BRAZIL_DDI_LENGTH)
    }

    return numericValue.slice(0, PHONE_MAX_LENGTH)
  },
}

export const currency = {
  format: (value: string | number) => {
    return formatNumber(value, {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: FRACTION_DIGITS_2,
    })
  },
  parse: parseToFloat,
}

function isCurrencyZeroLike(value: string | number): boolean {
  if (typeof value === 'number') {
    return value <= 0
  }

  const normalized = String(value).trim()

  if (normalized === '') {
    return true
  }

  return normalized === '0' || normalized === '0.00' || normalized === '0,00'
}

export const currencyOptional = {
  format: (value: string | number) => {
    if (isCurrencyZeroLike(value)) {
      return ''
    }

    return currency.format(value)
  },
  parse: parseToFloat,
}

export const decimal = {
  format: (value: string | number) => {
    return formatNumber(value, { minimumFractionDigits: FRACTION_DIGITS_2 })
  },
  parse: parseToFloat,
}

export const date = {
  format: (value: string) => {
    let clean = onlyNumbers(value).slice(0, DATE_LENGTH)

    // Auto-prefix day with 0 if it's unambiguous (> 3)
    if (clean.length === LENGTH_1 && parseInt(clean.charAt(0), RADIX_10) > MIN_UNAMBIGUOUS_DAY) {
      clean = `0${clean}`
    }

    // Validate day (01-31)
    if (clean.length >= DATE_SLICE_DAY) {
      let day = parseInt(clean.slice(0, DATE_SLICE_DAY), RADIX_10)
      if (day > DAYS_IN_MONTH_MAX) day = DAYS_IN_MONTH_MAX
      if (day === 0 && clean.length >= DATE_SLICE_DAY) day = MIN_DAY
      clean = day.toString().padStart(PAD_LENGTH_2, '0') + clean.slice(DATE_SLICE_DAY)
    }

    // Auto-prefix month with 0 if it's unambiguous (> 1)
    if (
      clean.length === DATE_HALF_LENGTH &&
      parseInt(clean.charAt(DATE_MONTH_PREFIX_INDEX), RADIX_10) > MIN_UNAMBIGUOUS_MONTH
    ) {
      clean = `${clean.slice(0, DATE_MONTH_PREFIX_INDEX)}0${clean.charAt(DATE_MONTH_PREFIX_INDEX)}`
    }

    // Validate month (01-12) and refine day if necessary
    if (clean.length >= DATE_SLICE_MONTH) {
      let day = parseInt(clean.slice(0, DATE_SLICE_DAY), RADIX_10)
      let month = parseInt(clean.slice(DATE_SLICE_DAY, DATE_SLICE_MONTH), RADIX_10)

      if (month > MONTHS_IN_YEAR) month = MONTHS_IN_YEAR
      if (month === 0) month = MIN_MONTH

      // Refine day based on month
      const months30 = MONTHS_30_DAYS
      if (month === MONTH_FEBRUARY) {
        if (day > MAX_DAY_FEBRUARY) day = MAX_DAY_FEBRUARY // Leap year check requires year, but 29 is a safe max until then
      } else if (months30.includes(month)) {
        if (day > MAX_DAY_30) day = MAX_DAY_30
      }

      clean =
        day.toString().padStart(PAD_LENGTH_2, '0') +
        month.toString().padStart(PAD_LENGTH_2, '0') +
        clean.slice(DATE_SLICE_MONTH)
    }

    // Validate year (range 1900-2099) and leap year check
    if (clean.length >= DATE_LENGTH) {
      let day = parseInt(clean.slice(0, DATE_SLICE_DAY), RADIX_10)
      const month = parseInt(clean.slice(DATE_SLICE_DAY, DATE_SLICE_MONTH), RADIX_10)
      const year = parseInt(clean.slice(DATE_SLICE_MONTH, DATE_LENGTH), RADIX_10)

      // Final leap year check for February
      if (month === MONTH_FEBRUARY) {
        const isLeap =
          (year % LEAP_YEAR_MOD_4 === 0 && year % LEAP_YEAR_MOD_100 !== 0) ||
          year % LEAP_YEAR_MOD_400 === 0
        const maxFeb = isLeap ? MAX_DAY_FEBRUARY : MAX_DAY_FEBRUARY_NON_LEAP
        if (day > maxFeb) day = maxFeb
      }

      clean =
        day.toString().padStart(PAD_LENGTH_2, '0') +
        month.toString().padStart(PAD_LENGTH_2, '0') +
        year.toString()
    }

    return clean
      .replace(/(\d{2})(\d)/, '$1/$2')
      .replace(/(\d{2})(\d)/, '$1/$2')
      .replace(/(\d{4})\d+?$/, '$1')
  },
  parse: parseToNumbers,
}

export const creditCard = {
  format: (value: string) => {
    return onlyNumbers(value)
      .slice(0, CREDIT_CARD_LENGTH)
      .replace(/(\d{4})(\d)/, '$1 $2')
      .replace(/(\d{4})(\d)/, '$1 $2')
      .replace(/(\d{4})(\d)/, '$1 $2')
      .replace(/(\d{4})\d+?$/, '$1')
  },
  parse: parseToNumbers,
}

export const time = {
  format: (value: string) => {
    let clean = onlyNumbers(value).slice(0, TIME_LENGTH)

    // Auto-prefix hours with 0 if it's unambiguous (> 2)
    if (clean.length === LENGTH_1 && parseInt(clean.charAt(0), RADIX_10) > MIN_UNAMBIGUOUS_HOUR) {
      clean = `0${clean}`
    }

    // Validate hours (00-23)
    if (clean.length >= TIME_SLICE_START) {
      let hours = parseInt(clean.slice(0, TIME_SLICE_START), RADIX_10)
      if (hours > MAX_HOURS) hours = MAX_HOURS
      clean = hours.toString().padStart(PAD_LENGTH_2, '0') + clean.slice(TIME_SLICE_START)
    }

    // Auto-prefix minutes with 0 if it's unambiguous (> 5)
    if (
      clean.length === TIME_HALF_LENGTH &&
      parseInt(clean.charAt(TIME_SLICE_START), RADIX_10) > MIN_UNAMBIGUOUS_MINUTE
    ) {
      clean = `${clean.slice(0, TIME_SLICE_START)}0${clean.charAt(TIME_SLICE_START)}`
    }

    // Validate minutes (00-59)
    if (clean.length >= TIME_LENGTH) {
      let minutes = parseInt(clean.slice(TIME_SLICE_START, TIME_LENGTH), RADIX_10)
      if (minutes > MAX_MINUTES) minutes = MAX_MINUTES
      clean = clean.slice(0, TIME_SLICE_START) + minutes.toString().padStart(PAD_LENGTH_2, '0')
    }

    return clean.replace(/(\d{2})(\d)/, '$1:$2').replace(/(:\d{2})\d+?$/, '$1')
  },
  parse: parseToNumbers,
}

export const number = {
  format: (value: string) => onlyNumbers(value).slice(0, MAX_GENERIC_NUMBER_LENGTH),
  parse: (value: string) => onlyNumbers(value).slice(0, MAX_GENERIC_NUMBER_LENGTH),
}

export const email = {
  format: (value: string) => value.toLowerCase().replace(/\s/g, ''),
  parse: (value: string) => value.toLowerCase().replace(/\s/g, ''),
}

export const percentage = {
  format: (value: string) => {
    const formatted = formatNumber(value, {
      minimumFractionDigits: FRACTION_DIGITS_2,
      maximumFractionDigits: FRACTION_DIGITS_2,
    })
    return formatted ? `${formatted}%` : ''
  },
  parse: parseToFloat,
}

export const licensePlate = {
  format: (value: string) => {
    const clean = onlyAlphanumeric(value).toUpperCase().slice(0, LICENSE_PLATE_LENGTH)

    // Legacy: LLL-NNNN (Index 3 is a digit, index 4 is a digit)
    // Mercosul: LLLNLNN (Index 3 is a digit, index 4 is a letter)

    // We add the hyphen for the legacy pattern.
    // At 4 chars, we don't know for sure, but we show it for better visual feedback
    // matching the user expectation ("como acontece com o CEP").
    if (clean.length >= PLATE_HYPHEN_INDEX + 1) {
      const isDigit4 = /\d/.test(clean[PLATE_HYPHEN_INDEX] ?? '')

      if (isDigit4) {
        // At 5+ chars, we can definitively distinguish Legacy from Mercosul
        if (clean.length >= MERCOSUR_OLD_PLATE_INDEX + 1) {
          const isOld = /\d/.test(clean[MERCOSUR_OLD_PLATE_INDEX] ?? '')
          if (isOld) {
            return `${clean.slice(0, PLATE_HYPHEN_INDEX)}-${clean.slice(PLATE_HYPHEN_INDEX)}`
          }
          return clean // Mercosul LLLNLNN
        }

        // At exactly 4 chars, show hyphen as a hint of the legacy pattern
        return `${clean.slice(0, PLATE_HYPHEN_INDEX)}-${clean.slice(PLATE_HYPHEN_INDEX)}`
      }
    }

    return clean
  },
  parse: parseToAlphanumeric,
}

// --- License Plate Constants & Validator ---

export const PLACAS_RANGE = [
  ['AAA0001', 'BEZ9999', 'PR', '02/1990'],
  ['BFA0001', 'GKI9999', 'SP', '10/1991'],
  ['GKJ0001', 'HOK9999', 'MG', '09/1991'],
  ['HOL0001', 'HQE9999', 'MA', '01/1992'],
  ['HQF0001', 'HTW9999', 'MS', '03/1992'],
  ['HTX0001', 'HZA9999', 'CE', '10/1992'],
  ['HZB0001', 'IAP9999', 'SE', '09/1993'],
  ['IAQ0001', 'JDO9999', 'RS', '03/1992'],
  ['JDP0001', 'JKR9999', 'DF', '04/1994'],
  ['JKS0001', 'JSZ9999', 'BA', '08/1993'],
  ['JTA0001', 'JWE9999', 'PA', '07/1993'],
  ['JWF0001', 'JXY9999', 'AM', '09/1993'],
  ['JXZ0001', 'KAU9999', 'MT', '09/1993'],
  ['KAV0001', 'KFC9999', 'GO', '05/1994'],
  ['KFD0001', 'KME9999', 'PE', '08/1994'],
  ['KMF0001', 'LVE9999', 'RJ', '05/1994'],
  ['LVF0001', 'LWQ9999', 'PI', '04/1996'],
  ['LWR0001', 'MMM9999', 'SC', '06/1996'],
  ['MMN0001', 'MOW9999', 'PB', '07/1996'],
  ['MOX0001', 'MTZ9999', 'ES', '12/1995'],
  ['MUA0001', 'MVK9999', 'AL', '08/1996'],
  ['MVL0001', 'MXG9999', 'TO', '11/1996'],
  ['MXH0001', 'MZM9999', 'RN', '06/1998'],
  ['MZN0001', 'NAG9999', 'AC', '06/1998'],
  ['NAH0001', 'NBA9999', 'RR', '07/1998'],
  ['NBB0001', 'NEH9999', 'RO', '07/1998'],
  ['NEI0001', 'NFB9999', 'AP', '09/1998'],
  ['NFC0001', 'NGZ9999', 'GO', '08/2003'],
  ['NHA0001', 'NHT9999', 'MA', '12/2006'],
  ['NHU0001', 'NIX9999', 'PI', '05/2007'],
  ['NIY0001', 'NJW9999', 'MT', '10/2007'],
  ['NJX0001', 'NLU9999', 'GO', '11/2007'],
  ['NLV0001', 'NMO9999', 'AL', '01/2008'],
  ['NMP0001', 'NNI9999', 'MA', '05/2008'],
  ['NNJ0001', 'NOH9999', 'RN', '07/2008'],
  ['NOI0001', 'NPB9999', 'AM', '07/2008'],
  ['NPC0001', 'NPQ9999', 'MT', '09/2008'],
  ['NPR0001', 'NQK9999', 'PB', '11/2008'],
  ['NQL0001', 'NRE9999', 'CE', '12/2008'],
  ['NRF0001', 'NSD9999', 'MS', '11/2009'],
  ['NSE0001', 'NTC9999', 'PA', '11/2009'],
  ['NTD0001', 'NTW9999', 'BA', '02/2010'],
  ['NTX0001', 'NUG9999', 'MT', '03/2010'],
  ['NUH0001', 'NUL9999', 'RR', '06/2010'],
  ['NUM0001', 'NVF9999', 'CE', '06/2010'],
  ['NVG0001', 'NVN9999', 'SE', '03/2010'],
  ['NVO0001', 'NWR9999', 'GO', '03/2010'],
  ['NWS0001', 'NXQ9999', 'MA', '12/2011'],
  ['NXR0001', 'NXT9999', 'AC', '05/2011'],
  ['NXU0001', 'NXW9999', 'PE', '07/2010'],
  ['NXX0001', 'NYG9999', 'MG', '10/2011'],
  ['NYH0001', 'NZZ9999', 'BA', '10/2010'],
  ['OAA0001', 'OAO9999', 'AM', '12/2010'],
  ['OAP0001', 'OBS9999', 'MT', '05/2011'],
  ['OBT0001', 'OCA9999', 'PA', '07/2011'],
  ['OCB0001', 'OCU9999', 'CE', '02/2011'],
  ['OCV0001', 'ODT9999', 'ES', '05/2011'],
  ['ODU0001', 'OEI9999', 'PI', '09/2011'],
  ['OEJ0001', 'OES9999', 'SE', '08/2011'],
  ['OET0001', 'OFH9999', 'PB', '04/2011'],
  ['OFI0001', 'OFW9999', 'PA', '03/2011'],
  ['OFX0001', 'OGG9999', 'PB', '06/2011'],
  ['OGH0001', 'OHA9999', 'GO', '04/2011'],
  ['OHB0001', 'OHK9999', 'AL', '09/2011'],
  ['OHL0001', 'OHW9999', 'RO', '11/2011'],
  ['OHX0001', 'OIQ9999', 'CE', '12/2011'],
  ['OIR0001', 'OJQ9999', 'MA', '01/2012'],
  ['OJR0001', 'OKC9999', 'RN', '04/2012'],
  ['OKD0001', 'OKH9999', 'SC', '02/2014'],
  ['OKI0001', 'OLG9999', 'BA', '10/2011'],
  ['OLH0001', 'OLN9999', 'TO', '02/2012'],
  ['OLO0001', 'OMH9999', 'MG', '03/2012'],
  ['OMI0001', 'OOF9999', 'GO', '04/2012'],
  ['OOG0001', 'OOU9999', 'MS', '02/2012'],
  ['OOV0001', 'ORC9999', 'MG', '06/2012'],
  ['ORD0001', 'ORM9999', 'AL', '01/2012'],
  ['ORN0001', 'OSV9999', 'CE', '07/2012'],
  ['OSW0001', 'OTZ9999', 'PA', '08/2012'],
  ['OUA0001', 'OUE9999', 'PI', '11/2012'],
  ['OUF0001', 'OVD9999', 'BA', '12/2012'],
  ['OVE0001', 'OVF9999', 'ES', '12/2012'],
  ['OVG0001', 'OVG9999', 'AC', '05/2013'],
  ['OVH0001', 'OVL9999', 'ES', '07/2013'],
  ['OVM0001', 'OVV9999', 'DF', '11/2013'],
  ['OVW0001', 'OVY9999', 'PI', '10/2013'],
  ['OVZ0001', 'OWG9999', 'RN', '06/2013'],
  ['OWH0001', 'OXK9999', 'MG', '10/2013'],
  ['OXL0001', 'OXL9999', 'RO', '11/2013'],
  ['OXM0001', 'OXM9999', 'AM', '11/2013'],
  ['OXN0001', 'OXN9999', 'AL', '11/2013'],
  ['OXO0001', 'OXO9999', 'PB', '11/2013'],
  ['OXP0001', 'OXP9999', 'AC', '12/2013'],
  ['OXQ0001', 'OXZ9999', 'MA', '04/2014'],
  ['OYA0001', 'OYC9999', 'TO', '11/2013'],
  ['OYD0001', 'OYK9999', 'ES', '12/2013'],
  ['OYL0001', 'OYZ9999', 'PE', '02/2014'],
  ['OZA0001', 'OZA9999', 'CE', '01/2014'],
  ['OZB0001', 'OZB9999', 'SE', '01/2014'],
  ['OZC0001', 'OZV9999', 'BA', '03/2014'],
  ['OZW0001', 'PBZ9999', 'DF', '05/2014'],
  ['PCA0001', 'PED9999', 'PE', '12/2014'],
  ['PEE0001', 'PFQ9999', 'PE', '09/2010'],
  ['PFR0001', 'PGK9999', 'PE', '07/2012'],
  ['PGL0001', 'PGU9999', 'PE', '10/2013'],
  ['PGV0001', 'PGZ9999', 'PE', '12/2014'],
  ['PHA0001', 'PHZ9999', 'AM', '06/2014'],
  ['PIA0001', 'PIZ9999', 'PI', '06/2014'],
  ['PJA0001', 'PLZ9999', 'BA', '10/2014'],
  ['PMA0001', 'POZ9999', 'CE', '06/2014'],
  ['PPA0001', 'PPZ9999', 'ES', '08/2014'],
  ['PQA0001', 'PRZ9999', 'GO', '03/2015'],
  ['PSA0001', 'PTZ9999', 'MA', '12/2014'],
  ['PUA0001', 'PZZ9999', 'MG', '05/2014'],
  ['QAA0001', 'QAZ9999', 'MS', '12/2014'],
  ['QBA0001', 'QCZ9999', 'MT', '05/2014'],
  ['QDA0001', 'QEZ9999', 'PA', '10/2014'],
  ['QFA0001', 'QFZ9999', 'PB', '05/2014'],
  ['QGA0001', 'QGZ9999', 'RN', '09/2014'],
  ['QHA0001', 'QJZ9999', 'SC', '05/2014'],
  ['QKA0001', 'QKM9999', 'TO', '11/2014'],
  ['QKN0001', 'QKZ9999', 'SE', '06/2014'],
  ['QLA0001', 'QLM9999', 'AL', '09/2015'],
  ['QLN0001', 'QLT9999', 'AP', '01/2015'],
  ['QLU0001', 'QLZ9999', 'AC', '08/2014'],
  ['QMA0001', 'QMP9999', 'SE', '03/2017'],
  ['QMQ0001', 'QQZ9999', 'MG', '07/2017'],
  ['QRA0001', 'QRA9999', 'RO', '11/2017'],
  ['QRB0001', 'QRZ9999', 'ES', '05/2018'],
  ['QSA0001', 'QSZ9999', 'PB', '04/2018'],
  ['QTA0001', 'QTB9999', 'RO', '08/2018'],
  ['QTC0001', 'RIN9999', 'BR', '00/0000'],
  ['RIO0001', 'RIO9999', 'RJ', '09/2018'],
  ['RLA0001', 'SAU9999', 'BR', '00/0000'],
  ['SAV0001', 'SAV9999', 'SP', '09/2009'],
]

export const PLACAS_INVALID = { start: 'SAW0001', end: 'ZZZ9999' }

/**
 * Validates a license plate according to Brazilian, Mercosul and range rules.
 */
export function validateLicensePlate(value: string, incluiMercosulInternacional = false): boolean {
  const clean = onlyAlphanumeric(value).toUpperCase()

  const regex = {
    legadoBR: /^[A-Z]{3}[0-9]{4}$/,
    mercosulBR: /^[A-Z]{3}[0-9]{1}[A-Z]{1}[0-9]{2}$/,
    mercosulAR: /^[A-Z]{2}[0-9]{3}[A-Z]{2}$|^[A-Z]{1}[0-9]{3}[A-Z]{3}$/,
    mercosulBO: /^[A-Z]{2}[0-9]{5}$/,
    mercosulPY: /^[A-Z]{4}[0-9]{3}$|^[0-9]{3}[A-Z]{4}$/,
    mercosulUY: /^[A-Z]{3}[0-9]{4}$/,
  }

  const isLegadoBRInvalid = clean >= PLACAS_INVALID.start && clean <= PLACAS_INVALID.end

  if (
    (regex.legadoBR.test(clean) && !isLegadoBRInvalid) ||
    regex.mercosulBR.test(clean) ||
    (incluiMercosulInternacional &&
      (regex.mercosulAR.test(clean) ||
        regex.mercosulBO.test(clean) ||
        regex.mercosulPY.test(clean) ||
        regex.mercosulUY.test(clean)))
  ) {
    return true
  }

  return false
}

// --- Validators ---

export const validateCpf = (value: string): boolean => {
  const clean = onlyNumbers(value)
  if (clean.length !== CPF_LENGTH) return false
  if (/^(\d)\1+$/.test(clean)) return false

  const CPF_FIRST_SEGMENT_LIMIT = 9
  const CPF_SECOND_SEGMENT_LIMIT = 10

  let sum = 0
  let remainder: number

  for (let i = 1; i <= CPF_FIRST_SEGMENT_LIMIT; i++) {
    sum = sum + parseInt(clean.substring(i - 1, i), RADIX_10) * (CPF_WEIGHT_START_1 - i)
  }
  remainder = (sum * CPF_CHECK_DIGIT_MULTIPLIER) % CPF_VALIDATION_MOD

  if (remainder === CPF_CHECK_DIGIT_MULTIPLIER || remainder === CPF_VALIDATION_MOD) remainder = 0
  if (remainder !== parseInt(clean.substring(CPF_DIGIT_1_INDEX, CPF_DIGIT_1_INDEX + 1), RADIX_10))
    return false

  sum = 0
  for (let i = 1; i <= CPF_SECOND_SEGMENT_LIMIT; i++) {
    sum = sum + parseInt(clean.substring(i - 1, i), RADIX_10) * (CPF_WEIGHT_START_2 - i)
  }
  remainder = (sum * CPF_CHECK_DIGIT_MULTIPLIER) % CPF_VALIDATION_MOD

  if (remainder === CPF_CHECK_DIGIT_MULTIPLIER || remainder === CPF_VALIDATION_MOD) remainder = 0
  if (remainder !== parseInt(clean.substring(CPF_DIGIT_2_INDEX, CPF_DIGIT_2_INDEX + 1), RADIX_10))
    return false

  return true
}

export const validateCnpj = (value: string): boolean => {
  const clean = onlyAlphanumeric(value).toUpperCase()
  if (clean.length !== CNPJ_LENGTH) return false

  // Regex according to the new specification: 12 alphanumeric + 2 numeric check digits
  if (!/^([A-Z0-9]){12}([0-9]){2}$/.test(clean)) return false

  // Reject all zeros
  if (clean === CNPJ_ALL_ZEROS) return false

  // Helper to get value of char (ASCII - 48)
  // 0-9: 48-57 -> 0-9
  // A-Z: 65-90 -> 17-42
  const getCharValue = (char: string) => char.charCodeAt(0) - CHAR_CODE_ZERO

  // Calculate first check digit
  // Weights: 5,4,3,2,9,8,7,6,5,4,3,2
  const weights1 = CNPJ_WEIGHTS_1
  let sum = 0
  for (let i = 0; i < CNPJ_UNITS_ONLY_LENGTH; i++) {
    sum += getCharValue(clean.charAt(i)) * (weights1[i] || 0)
  }
  let remainder = sum % CNPJ_VALIDATION_MOD
  const digit1 = remainder < CNPJ_VALIDATION_LIMIT ? 0 : CNPJ_VALIDATION_MOD - remainder

  if (digit1 !== parseInt(clean.charAt(CNPJ_UNITS_ONLY_LENGTH), RADIX_10)) return false

  // Calculate second check digit
  // Weights: 6,5,4,3,2,9,8,7,6,5,4,3,2
  const weights2 = CNPJ_WEIGHTS_2
  sum = 0
  for (let i = 0; i < CNPJ_WITH_DIGIT_1_LENGTH; i++) {
    // Note: includes the calculated first digit (which is at index 12)
    sum += getCharValue(clean.charAt(i)) * (weights2[i] || 0)
  }
  remainder = sum % CNPJ_VALIDATION_MOD
  const digit2 = remainder < CNPJ_VALIDATION_LIMIT ? 0 : CNPJ_VALIDATION_MOD - remainder

  if (digit2 !== parseInt(clean.charAt(CNPJ_WITH_DIGIT_1_LENGTH), RADIX_10)) return false

  return true
}

export const validateCpfCnpj = (value: string): boolean => {
  const clean = onlyAlphanumeric(value)
  if (clean.length === CPF_LENGTH) return validateCpf(clean)
  if (clean.length === CNPJ_LENGTH) return validateCnpj(clean)
  return false
}

/**
 * Validates a Brazilian phone number. Accepts landline (10 digits) or mobile (11 digits).
 * Strips non-numeric characters before checking length.
 */
export const validatePhone = (value: string): boolean => {
  const clean = onlyNumbers(value)
  return clean.length === PHONE_LANDLINE_LENGTH || clean.length === PHONE_MAX_LENGTH
}

/**
 * Validates a Brazilian CEP. Strips non-numeric characters and checks for exactly 8 digits.
 */
export const validateCep = (value: string): boolean => {
  return onlyNumbers(value).length === CEP_LENGTH
}

// Exporting a unified masks object for easy consumption
export const masks = {
  cpf,
  cnpj,
  cpfCnpj,
  cep,
  phone,
  currency,
  currencyOptional,
  date,
  creditCard,
  number,
  email,
  decimal,
  time,
  percentage,
  licensePlate,
}

export type MaskType = keyof typeof masks
